import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaCrown,
  FaBuilding,
  FaCheckCircle,
  FaTimesCircle,
  FaEdit,
} from "react-icons/fa";

import { useGetCoachingCenterDetailsQuery } from "../../slices/coachingCenterApiSlice";

const CoachingCenterDetailsScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data: center,
    isLoading,
    error,
  } = useGetCoachingCenterDetailsQuery(id);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-lg text-gray-600">
        Loading coaching center...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="rounded-xl bg-red-100 text-red-700 px-6 py-4 shadow-lg">
          {error?.data?.message || error.error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Top Buttons */}

        <div className="flex justify-between items-center mb-6">
          <Link
            to="/admin/coachingcenters"
            className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 shadow hover:bg-blue-50 text-blue-600 transition"
          >
            ← Back
          </Link>

          <button
            onClick={() => navigate(`/coaching-centers/${center._id}/edit`)}
            className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 font-semibold text-white shadow hover:bg-amber-600 transition"
          >
            <FaEdit />
            Edit
          </button>
        </div>

        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/30 overflow-hidden">
          {/* Header */}

          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-white">
            <h1 className="text-4xl font-bold flex items-center gap-3">
              <FaBuilding />
              {center.name}
            </h1>

            <p className="mt-2 text-blue-100">Coaching Center Information</p>
          </div>

          {/* Body */}

          <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Contact Person */}

            <div>
              <label className="text-sm font-semibold text-gray-500">
                Contact Person
              </label>

              <div className="mt-2 flex items-center gap-3 text-lg text-gray-800">
                <FaUser className="text-blue-600" />
                {center.contactPerson}
              </div>
            </div>

            {/* Phone */}

            <div>
              <label className="text-sm font-semibold text-gray-500">
                Phone
              </label>

              <div className="mt-2 flex items-center gap-3 text-lg text-gray-800">
                <FaPhone className="text-green-600" />
                {center.phone}
              </div>
            </div>

            {/* Email */}

            <div>
              <label className="text-sm font-semibold text-gray-500">
                Email
              </label>

              <div className="mt-2 flex items-center gap-3 text-lg text-gray-800">
                <FaEnvelope className="text-red-500" />
                {center.email || "N/A"}
              </div>
            </div>

            {/* Subscription */}

            <div>
              <label className="text-sm font-semibold text-gray-500">
                Subscription Plan
              </label>

              <div className="mt-2 flex items-center gap-3 text-lg text-gray-800 capitalize">
                <FaCrown className="text-yellow-500" />
                {center.subscriptionPlan || "N/A"}
              </div>
            </div>

            {/* Expiry */}

            <div>
              <label className="text-sm font-semibold text-gray-500">
                Subscription Expiry
              </label>

              <div className="mt-2 flex items-center gap-3 text-lg text-gray-800">
                <FaCalendarAlt className="text-indigo-600" />
                {center.subscriptionExpiry
                  ? new Date(center.subscriptionExpiry).toLocaleDateString()
                  : "N/A"}
              </div>
            </div>

            {/* Status */}

            <div>
              <label className="text-sm font-semibold text-gray-500">
                Status
              </label>

              <div className="mt-2 flex items-center gap-3 text-lg">
                {center.isActive ? (
                  <>
                    <FaCheckCircle className="text-green-600" />
                    <span className="text-green-700 font-semibold">Active</span>
                  </>
                ) : (
                  <>
                    <FaTimesCircle className="text-red-600" />
                    <span className="text-red-700 font-semibold">Inactive</span>
                  </>
                )}
              </div>
            </div>

            {/* Address */}

            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-gray-500">
                Address
              </label>

              <div className="mt-2 flex items-start gap-3 text-lg text-gray-800">
                <FaMapMarkerAlt className="text-red-500 mt-1" />

                <span>{center.address || "N/A"}</span>
              </div>
            </div>

            {/* Created By */}

            <div>
              <label className="text-sm font-semibold text-gray-500">
                Created By
              </label>

              <div className="mt-2 text-lg text-gray-800">
                <p>{center.createdBy?.name || "N/A"}</p>

                <p className="text-sm text-gray-500">
                  {center.createdBy?.phone}
                </p>
              </div>
            </div>

            {/* Created At */}

            <div>
              <label className="text-sm font-semibold text-gray-500">
                Created On
              </label>

              <div className="mt-2 text-lg text-gray-800">
                {new Date(center.createdAt).toLocaleString()}
              </div>
            </div>

            {/* Updated At */}

            <div>
              <label className="text-sm font-semibold text-gray-500">
                Last Updated
              </label>

              <div className="mt-2 text-lg text-gray-800">
                {new Date(center.updatedAt).toLocaleString()}
              </div>
            </div>

            {/* Slug */}

            <div>
              <label className="text-sm font-semibold text-gray-500">
                Slug
              </label>

              <div className="mt-2 rounded-lg bg-gray-100 px-4 py-3 font-mono text-sm text-gray-700 break-all">
                {center.slug}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachingCenterDetailsScreen;
